import { useCallback } from 'react';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { FormImagePrompt, UploadImageType } from '@/app/types';
import { getBackfillImageId } from '@/app/utils/client';
import { useTransitionSync } from './use-transition-sync';

/**
 * img2video 回填专用 Hook
 *
 * 职责：
 * 1. 图片回填：将图片回填到 img2video 表单
 * 2. 转场同步：回填后自动同步转场数量（复用 useTransitionSync）
 *
 * 与 useImg2VideoForm 的区别：
 * - 只包含回填相关功能，不包含图片上传、删除等完整管理功能
 * - 更轻量级，适合在回填场景中使用
 * - 直接操作 store，保持与原有实现的一致性
 */
export const useImg2VideoBackfill = () => {
  const { updateFormParams } = useStore((state) => ({
    updateFormParams: state.updateFormParams,
  }));
  const store = useStoreApi();

  // 复用转场同步逻辑
  const { syncTransitionsAfterImageCountChange } = useTransitionSync();

  /**
   * 将图片回填到 img2video：
   * - 自动选择第一个空位置或追加（最多 MAX_IMG2V_FRAMES 外部应控制）
   * - 写入带 backfill 前缀的 id，确保页面可识别并执行宽高比检查流程
   * - 同步 multiFrameTransitions
   */
  const handleBackfillImagePromptToImg2Video = useCallback(
    (imagePrompt: UploadImageType, targetIndex?: number) => {
      const formParams = store.getState().img2videoFormParams;
      const prompts = formParams.imagePrompts || [];
      const emptyIndex =
        typeof targetIndex === 'number' ? targetIndex : prompts.findIndex((item) => !item || !item.prompt);
      const resolvedIndex = emptyIndex !== -1 ? emptyIndex : prompts.length;
      const id = getBackfillImageId(resolvedIndex);

      const nextPrompts: (FormImagePrompt | null)[] = [...prompts];
      nextPrompts[resolvedIndex] = {
        id,
        status: 'done',
        prompt: imagePrompt,
      };
      updateFormParams('img2video', 'imagePrompts', nextPrompts);

      // 同步转场数量
      const newValidImageCount = nextPrompts.filter((p) => !!p?.prompt).length;
      syncTransitionsAfterImageCountChange(newValidImageCount);

      return { index: resolvedIndex, id };
    },
    [store, syncTransitionsAfterImageCountChange, updateFormParams],
  );

  return {
    handleBackfillImagePromptToImg2Video,
  };
};
